import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from './ui/button.jsx';
import { Input } from './ui/input.jsx';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card.jsx';
import { Badge } from './ui/badge.jsx';
import PaginationWrapper from './PaginationWrapper';
import { 
  Users, 
  Building, 
  UserPlus, 
  Search,
  Trash2,
  Edit,
  Key,
  Eye,
  Shield,
  BarChart3,
  Calendar,
  Activity
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const SuperadminDashboard = () => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [activities, setActivities] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // Pagination states
  const [usersPagination, setUsersPagination] = useState({
    currentPage: 1,
    total: 0,
    skip: 0,
    limit: 20,
    has_more: false
  });
  const [companiesPagination, setCompaniesPagination] = useState({
    currentPage: 1,
    total: 0,
    skip: 0,
    limit: 20,
    has_more: false
  });
  const [activitiesPagination, setActivitiesPagination] = useState({
    currentPage: 1,
    total: 0,
    skip: 0,
    limit: 20,
    has_more: false
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [userForm, setUserForm] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'user'
  });
  const [editUserForm, setEditUserForm] = useState({
    email: '',
    full_name: '',
    role: 'user'
  });
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [showAssignCompany, setShowAssignCompany] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({
    name: '',
    login_url: '',
    username: '',
    password: '',
    user_id: ''
  });
  const [assignForm, setAssignForm] = useState({
    company_id: '',
    new_user_id: ''
  });

  useEffect(() => {
    fetchStats();
    fetchUsers();
    fetchCompanies();
    fetchActivities();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get('/superadmin/stats');
      setStats(response.data);
    } catch (error) {
      toast.error('Error fetching system statistics');
    }
  };

  const fetchUsers = async (page = 1) => {
    try {
      const skip = (page - 1) * 20;
      const response = await axios.get(`/superadmin/users?search=${searchTerm}&skip=${skip}&limit=20`);
      
      if (response.data.users) {
        setUsers(response.data.users);
        setUsersPagination({
          currentPage: page,
          total: response.data.total,
          skip: response.data.skip,
          limit: response.data.limit,
          has_more: response.data.has_more
        });
      } else {
        // Fallback for old API response
        setUsers(response.data);
      }
    } catch (error) {
      toast.error('Error fetching users');
    }
  };

  const fetchCompanies = async (page = 1) => {
    try {
      const skip = (page - 1) * 20;
      const response = await axios.get(`/superadmin/companies?skip=${skip}&limit=20`);
      
      if (response.data.companies) {
        setCompanies(response.data.companies);
        setCompaniesPagination({
          currentPage: page,
          total: response.data.total,
          skip: response.data.skip,
          limit: response.data.limit,
          has_more: response.data.has_more
        });
      } else {
        // Fallback for old API response
        setCompanies(response.data);
      }
    } catch (error) {
      toast.error('Error fetching companies');
    }
  };

  const fetchActivities = async (page = 1) => {
    try {
      const skip = (page - 1) * 20;
      const response = await axios.get(`/superadmin/activities?limit=20&skip=${skip}`);
      
      if (response.data.activities) {
        setActivities(response.data.activities);
        setActivitiesPagination({
          currentPage: page,
          total: response.data.total,
          skip: response.data.skip,
          limit: response.data.limit,
          has_more: response.data.has_more
        });
      } else {
        // Fallback for old API response
        setActivities(response.data);
      }
    } catch (error) {
      toast.error('Error fetching activities');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/superadmin/users', userForm);
      toast.success('User created successfully');
      setShowCreateUser(false);
      setUserForm({ email: '', full_name: '', password: '', role: 'user' });
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating user');
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditUserForm({
      email: user.email,
      full_name: user.full_name,
      role: user.role
    });
    setShowEditUser(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/superadmin/users/${selectedUser.id}`, editUserForm);
      toast.success('User updated successfully');
      setShowEditUser(false);
      setSelectedUser(null);
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating user');
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    if (!companyForm.user_id) {
      toast.error('Please select a user to assign the company to');
      return;
    }
    
    try {
      const { user_id, ...companyData } = companyForm;
      await axios.post(`/superadmin/companies?user_id=${user_id}`, companyData);
      toast.success('Company created successfully');
      setShowCreateCompany(false);
      setCompanyForm({ name: '', login_url: '', username: '', password: '', user_id: '' });
      fetchCompanies();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating company');
    }
  };

  const handleAssignCompany = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/superadmin/companies/${assignForm.company_id}/assign?new_user_id=${assignForm.new_user_id}`);
      toast.success('Company assigned successfully');
      setShowAssignCompany(false);
      setAssignForm({ company_id: '', new_user_id: '' });
      setSelectedCompany(null);
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error assigning company');
    }
  };

  const handleShowAssignCompany = (company) => {
    setSelectedCompany(company);
    setAssignForm({ company_id: company.id, new_user_id: '' });
    setShowAssignCompany(true);
  };

  // Page handlers
  const handleUsersPageChange = (page) => {
    setUsersPagination(prev => ({ ...prev, currentPage: page }));
    fetchUsers(page);
  };

  const handleCompaniesPageChange = (page) => {
    setCompaniesPagination(prev => ({ ...prev, currentPage: page }));
    fetchCompanies(page);
  };

  const handleActivitiesPageChange = (page) => {
    setActivitiesPagination(prev => ({ ...prev, currentPage: page }));
    fetchActivities(page);
  };

  // Activity deletion functions
  const handleDeleteActivity = async (activityId) => {
    if (window.confirm('Are you sure you want to delete this activity log?')) {
      try {
        await axios.delete(`/superadmin/activities/${activityId}`);
        toast.success('Activity deleted successfully');
        fetchActivities(activitiesPagination.currentPage);
      } catch (error) {
        toast.error('Error deleting activity');
      }
    }
  };

  const handleBulkDeleteActivities = async () => {
    const beforeDate = prompt('Delete all activities before date (YYYY-MM-DD format, leave empty to delete all):');
    
    let confirmMessage = 'Are you sure you want to delete ';
    if (beforeDate) {
      confirmMessage += `all activities before ${beforeDate}?`;
    } else {
      confirmMessage += 'ALL activity logs? This action cannot be undone!';
    }
    
    if (window.confirm(confirmMessage)) {
      try {
        let endpoint = '/superadmin/activities/';
        if (beforeDate) {
          endpoint += `bulk?before_date=${beforeDate}T23:59:59`;
        } else {
          endpoint += 'all';
        }
        
        const response = await axios.delete(endpoint);
        toast.success(response.data.message);
        fetchActivities(1);
        setActivitiesPagination(prev => ({ ...prev, currentPage: 1 }));
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error deleting activities');
      }
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This will also delete all their companies.')) {
      try {
        await axios.delete(`/superadmin/users/${userId}`);
        toast.success('User deleted successfully');
        fetchUsers();
        fetchStats();
      } catch (error) {
        toast.error('Error deleting user');
      }
    }
  };

  const handleResetPassword = async (userId) => {
    const newPassword = prompt('Enter new password for this user:');
    if (newPassword) {
      try {
        await axios.post(`/superadmin/users/${userId}/reset-password`, {
          new_password: newPassword
        });
        toast.success('Password reset successfully');
      } catch (error) {
        toast.error('Error resetting password');
      }
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (window.confirm('Are you sure you want to delete this company?')) {
      try {
        await axios.delete(`/superadmin/companies/${companyId}`);
        toast.success('Company deleted successfully');
        fetchCompanies();
        fetchStats();
      } catch (error) {
        toast.error('Error deleting company');
      }
    }
  };

  useEffect(() => {
    if (searchTerm) {
      const debounceTimer = setTimeout(() => {
        fetchUsers();
      }, 500);
      return () => clearTimeout(debounceTimer);
    } else {
      fetchUsers();
    }
  }, [searchTerm]);

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p>Loading superadmin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="bg-gradient-to-r from-red-600 to-red-700 p-2.5 rounded-xl shadow-lg">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Superadmin Panel</h1>
                <p className="text-sm text-gray-500">System Administration</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Badge variant="destructive" className="px-3 py-1">
                Administrator
              </Badge>
              <span className="text-sm text-gray-600">{user?.full_name}</span>
              <Button variant="outline" onClick={logout} size="sm">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="flex space-x-1 mb-8 bg-gray-100 p-1 rounded-lg w-fit">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'users', label: 'Users', icon: Users },
            { id: 'companies', label: 'Companies', icon: Building },
            { id: 'activities', label: 'Activities', icon: Activity }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-8">
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm">Total Users</p>
                      <p className="text-3xl font-bold">{stats.total_users}</p>
                    </div>
                    <Users className="h-8 w-8 text-blue-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-sm">Total Companies</p>
                      <p className="text-3xl font-bold">{stats.total_companies}</p>
                    </div>
                    <Building className="h-8 w-8 text-green-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm">Users Today</p>
                      <p className="text-3xl font-bold">{stats.users_registered_today}</p>
                    </div>
                    <Calendar className="h-8 w-8 text-purple-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-100 text-sm">Companies Today</p>
                      <p className="text-3xl font-bold">{stats.companies_added_today}</p>
                    </div>
                    <Building className="h-8 w-8 text-orange-200" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Users */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Users</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stats.recent_users.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{user.full_name}</p>
                        <p className="text-sm text-gray-600">{user.email}</p>
                      </div>
                      <div className="text-right">
                        <Badge variant={user.role === 'superadmin' ? 'destructive' : 'default'}>
                          {user.role}
                        </Badge>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(user.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Users Header */}
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
              <Button onClick={() => setShowCreateUser(true)} className="bg-blue-600 hover:bg-blue-700">
                <UserPlus className="h-4 w-4 mr-2" />
                Create User
              </Button>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Users List */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {users.map((userItem) => (
                        <tr key={userItem.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{userItem.full_name}</div>
                              <div className="text-sm text-gray-500">{userItem.email}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge variant={userItem.role === 'superadmin' ? 'destructive' : 'default'}>
                              {userItem.role}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(userItem.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {userItem.last_login ? new Date(userItem.last_login).toLocaleDateString() : 'Never'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                            <Button size="sm" variant="outline" onClick={() => handleEditUser(userItem)} title="Edit User">
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleResetPassword(userItem.id)} title="Reset Password">
                              <Key className="h-3 w-3" />
                            </Button>
                            {/* Don't allow superadmin to delete themselves */}
                            {userItem.id !== user?.id && (
                              <Button size="sm" variant="destructive" onClick={() => handleDeleteUser(userItem.id)} title="Delete User">
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            
            {/* Users Pagination */}
            {usersPagination.total > 0 && (
              <PaginationWrapper
                currentPage={usersPagination.currentPage}
                totalPages={Math.ceil(usersPagination.total / usersPagination.limit)}
                total={usersPagination.total}
                limit={usersPagination.limit}
                skip={usersPagination.skip}
                onPageChange={handleUsersPageChange}
                className="mt-6"
              />
            )}
          </div>
        )}

        {/* Companies Tab */}
        {activeTab === 'companies' && (
          <div className="space-y-6">
            {/* Companies Header */}
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Company Management</h2>
              <div className="flex space-x-2">
                <Button onClick={() => setShowCreateCompany(true)} className="bg-green-600 hover:bg-green-700">
                  <Building className="h-4 w-4 mr-2" />
                  Create Company
                </Button>
                <Button onClick={() => setShowAssignCompany(true)} variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50">
                  <Edit className="h-4 w-4 mr-2" />
                  Assign Company
                </Button>
              </div>
            </div>
            
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Login URL</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {companies.map((company) => (
                        <tr key={company.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{company.name}</div>
                            <div className="text-sm text-gray-500">Username: {company.username}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {company.user_id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <a href={company.login_url} target="_blank" rel="noopener noreferrer" 
                               className="text-blue-600 hover:text-blue-800 text-sm">
                              {company.login_url}
                            </a>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(company.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                            <Button size="sm" variant="outline" onClick={() => handleShowAssignCompany(company)} title="Assign to User">
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleDeleteCompany(company.id)} title="Delete Company">
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            
            {/* Companies Pagination */}
            {companiesPagination.total > 0 && (
              <PaginationWrapper
                currentPage={companiesPagination.currentPage}
                totalPages={Math.ceil(companiesPagination.total / companiesPagination.limit)}
                total={companiesPagination.total}
                limit={companiesPagination.limit}
                skip={companiesPagination.skip}
                onPageChange={handleCompaniesPageChange}
                className="mt-6"
              />
            )}
          </div>
        )}

        {/* Activities Tab */}
        {activeTab === 'activities' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">System Activity Monitoring</h2>
              <div className="flex space-x-2">
                <Button 
                  onClick={handleBulkDeleteActivities} 
                  variant="destructive" 
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete All Activities
                </Button>
              </div>
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle>Recent System Activities</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP Address</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {activities.map((activity) => {
                        const getActionColor = (action) => {
                          switch (action) {
                            case 'auto_login': return 'bg-purple-100 text-purple-800';
                            case 'copy_credentials': return 'bg-indigo-100 text-indigo-800';
                            case 'view': return 'bg-blue-100 text-blue-800';
                            case 'edit': return 'bg-yellow-100 text-yellow-800';
                            case 'delete': return 'bg-red-100 text-red-800';
                            case 'create': return 'bg-green-100 text-green-800';
                            default: return 'bg-gray-100 text-gray-800';
                          }
                        };

                        const getActionLabel = (action) => {
                          switch (action) {
                            case 'auto_login': return 'Auto Login';
                            case 'copy_credentials': return 'Copy Credentials';
                            case 'view': return 'View';
                            case 'edit': return 'Edit';
                            case 'delete': return 'Delete';
                            case 'create': return 'Create';
                            default: return action;
                          }
                        };

                        return (
                          <tr key={activity.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getActionColor(activity.action)}`}>
                                {getActionLabel(activity.action)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {activity.user_id.substring(0, 8)}...
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                              {activity.details || 'No details'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(activity.timestamp).toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {activity.ip_address || 'Unknown'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <Button 
                                size="sm" 
                                variant="destructive" 
                                onClick={() => handleDeleteActivity(activity.id)} 
                                title="Delete Activity"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {activities.length === 0 && (
                  <div className="p-12 text-center">
                    <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Activities Yet</h3>
                    <p className="text-gray-600">
                      System activities will appear here as users interact with their companies.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Activities Pagination */}
            {activitiesPagination.total > 0 && (
              <PaginationWrapper
                currentPage={activitiesPagination.currentPage}
                totalPages={Math.ceil(activitiesPagination.total / activitiesPagination.limit)}
                total={activitiesPagination.total}
                limit={activitiesPagination.limit}
                skip={activitiesPagination.skip}
                onPageChange={handleActivitiesPageChange}
                className="mt-6"
              />
            )}
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Create New User</h3>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <Input
                placeholder="Full Name"
                value={userForm.full_name}
                onChange={(e) => setUserForm({...userForm, full_name: e.target.value})}
                required
              />
              <Input
                type="email"
                placeholder="Email"
                value={userForm.email}
                onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                required
              />
              <Input
                type="password"
                placeholder="Password"
                value={userForm.password}
                onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                required
              />
              <select
                value={userForm.role}
                onChange={(e) => setUserForm({...userForm, role: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="user">Regular User</option>
                <option value="superadmin">Superadmin</option>
              </select>
              <div className="flex space-x-2">
                <Button type="submit" className="flex-1">Create User</Button>
                <Button type="button" variant="outline" onClick={() => setShowCreateUser(false)} className="flex-1">
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUser && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Edit User: {selectedUser.full_name}</h3>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <Input
                placeholder="Full Name"
                value={editUserForm.full_name}
                onChange={(e) => setEditUserForm({...editUserForm, full_name: e.target.value})}
                required
              />
              <Input
                type="email"
                placeholder="Email"
                value={editUserForm.email}
                onChange={(e) => setEditUserForm({...editUserForm, email: e.target.value})}
                required
              />
              <select
                value={editUserForm.role}
                onChange={(e) => setEditUserForm({...editUserForm, role: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="user">Regular User</option>
                <option value="superadmin">Superadmin</option>
              </select>
              <div className="flex space-x-2">
                <Button type="submit" className="flex-1">Update User</Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowEditUser(false);
                    setSelectedUser(null);
                  }} 
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Company Modal */}
      {showCreateCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Create Company</h3>
            <form onSubmit={handleCreateCompany} className="space-y-4">
              <Input
                placeholder="Company Name"
                value={companyForm.name}
                onChange={(e) => setCompanyForm({...companyForm, name: e.target.value})}
                required
              />
              <Input
                type="url"
                placeholder="Login URL"
                value={companyForm.login_url}
                onChange={(e) => setCompanyForm({...companyForm, login_url: e.target.value})}
                required
              />
              <Input
                placeholder="Username"
                value={companyForm.username}
                onChange={(e) => setCompanyForm({...companyForm, username: e.target.value})}
                required
              />
              <Input
                type="password"
                placeholder="Password"
                value={companyForm.password}
                onChange={(e) => setCompanyForm({...companyForm, password: e.target.value})}
                required
              />
              <select
                value={companyForm.user_id}
                onChange={(e) => setCompanyForm({...companyForm, user_id: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              >
                <option value="">Select User to Assign To</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.full_name} ({user.email})
                  </option>
                ))}
              </select>
              <div className="flex space-x-2">
                <Button type="submit" className="flex-1">Create Company</Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowCreateCompany(false);
                    setCompanyForm({ name: '', login_url: '', username: '', password: '', user_id: '' });
                  }} 
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Company Modal */}
      {showAssignCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              Assign Company{selectedCompany ? `: ${selectedCompany.name}` : ''}
            </h3>
            <form onSubmit={handleAssignCompany} className="space-y-4">
              {!selectedCompany && (
                <select
                  value={assignForm.company_id}
                  onChange={(e) => setAssignForm({...assignForm, company_id: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value="">Select Company</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              )}
              <select
                value={assignForm.new_user_id}
                onChange={(e) => setAssignForm({...assignForm, new_user_id: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              >
                <option value="">Select New Owner</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.full_name} ({user.email})
                  </option>
                ))}
              </select>
              <div className="flex space-x-2">
                <Button type="submit" className="flex-1">Assign Company</Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowAssignCompany(false);
                    setAssignForm({ company_id: '', new_user_id: '' });
                    setSelectedCompany(null);
                  }} 
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperadminDashboard;